<template>
  <base-input :rules="computedRules" :errors="errors">
    <div
      :class="[
        'relative border rounded-md focus-within:ring-1 ring-0 flex items-center transition-all ease-in-out duration-100 bg-white z-10 mt-1',
        `focus-within:ring-${focusColor} focus-within:border-${focusColor} border-${borderColor}`,
      ]"
    >
      <label :class="['absolute -top-2 left-2 -mt-px inline-block px-1 bg-white text-xs font-normal', `text-${labelColor}`]" v-if="label">{{ label }}</label>
      <div :class="`flex items-center text-gray-600 pl-3 -mr-1.5 font-thin sm:text-${size} flex-shrink-0 z-0`">USD</div>
      <input
        ref="inputRef"
        type="text"
        :class="['form-input flex-grow block w-full border-0 p-0 text-gray-900 focus:ring-0 font-light px-3 py-2  rounded-md ', `placeholder-${placeholderColor} sm:text-${size}`]"
        :placeholder="placeholder"
        :disabled="disabled"
      />
    </div>
  </base-input>
</template>

<script>
import { watch, onMounted, nextTick } from "vue";
import { useCurrencyInput, ValueScaling } from "vue-currency-input";
import BaseInput from "@/components/base/BaseInput";
import validatable from "@/mixins/validatable";

export default {
  name: "BaseCurrencyInput",
  mixins: [validatable],
  components: {
    BaseInput,
  },
  props: {
    modelValue: [Number, String], // formatted display value
    intValue: Number, // integer value: cents (precision 2) or whole dollars (precision 0)
    label: String,
    placeholder: String,
    precision: {
      type: Number,
      default: null,
    },
    min: {
      type: Number,
      default: null,
    },
    max: {
      type: Number,
      default: null,
    },
    rules: {
      type: Array,
      default: () => [],
    },
    required: Boolean,
    disabled: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: "sm",
    },
  },
  emits: ["update:modelValue", "update:intValue"],
  setup(props, { emit }) {
    const isDollarMode = props.precision === 0;
    const options = {
      currency: "USD",
      precision: isDollarMode ? 0 : 2,
      hideCurrencySymbolOnFocus: false,
      hideGroupingSeparatorOnFocus: false,
      valueScaling: isDollarMode ? undefined : ValueScaling.precision,
    };

    if (props.min != null || props.max != null) {
      options.valueRange = {
        min: props.min ?? undefined,
        max: props.max ?? undefined,
      };
    }

    const { inputRef, numberValue, formattedValue, setValue } = useCurrencyInput(options, false);
    onMounted(async () => {
      await nextTick();
      if (props.intValue != null) {
        setValue(props.intValue);
      }
    });

    // seed the input from the integer (cents) value supplied by the parent.
    // skip when the value already matches numberValue - that is the echo of
    // our own emit, and re-running setValue there desyncs the input on blur.
    watch(
      () => props.intValue,
      (value) => {
        if (value !== numberValue.value) {
          setValue(value ?? null);
        }
      },
      { immediate: true },
    );

    // propagate user edits back to the parent
    watch(numberValue, (value) => {
      emit("update:intValue", value);
      emit("update:modelValue", formattedValue.value);
    });

    return { inputRef, setValue };
  },
  methods: {
    reset() {
      this.isResetting = true;
      this.setValue(null);
    },
  },
  computed: {
    focusColor() {
      return this.hasError ? "red-500" : "primary-500";
    },
    borderColor() {
      return this.hasError ? "red-500" : "gray-200";
    },
    labelColor() {
      return this.hasError ? "red-500" : "gray-900";
    },
    placeholderColor() {
      return this.hasError ? "red-400" : "gray-500";
    },
    computedRules() {
      const baseRules = this.rules || [];
      const isDollarMode = this.precision === 0;

      const minMaxRule = () => {
        if (this.intValue == null) return true;
        // intValue is cents unless in whole-dollar mode
        const value = isDollarMode ? this.intValue : this.intValue / 100;

        if (this.min != null && value < this.min) {
          return `Minimum allowed amount is $${this.min}`;
        }

        if (this.max != null && value > this.max) {
          return `Maximum allowed amount is $${this.max}`;
        }

        if (isDollarMode && !Number.isInteger(value)) {
          return "Value must be a whole number";
        }

        return true;
      };

      return [...baseRules, minMaxRule];
    },
  },
};
</script>
